import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FiltersState, defaultSelected } from "./filters.state";
import {
  SelectedFilters,
  ListFilterKey,
  BooleanFilterKey,
  STRING_FILTER_KEYS,
  FiltersData,
} from "./filters.types";
import { getFilters, getGeneralFilters } from "./filters.thunk";
import {
  countGuardedSelections,
  isMinimumGuardedKey,
} from "@/app/utils/filters.utils";

const initialState: FiltersState = {
  data: null,
  generalData: null,
  status: "idle",
  generalStatus: "idle",
  error: null,
  selected: defaultSelected,
  serverSelected: defaultSelected,
  serverSelectedReady: false,
  serverSelectedSlug: null,
  userInteractionCount: 0,
  urlOwnedKeys: [],
  guardedContextSeeded: false,
};

/** Backend filtre yanıtındaki grup value'larını SelectedFilters şekline çevirir.
 *  Tüm key'ler yazılır — sidebar seçili durumu için serverSelected tam dolu olmalı.
 *  URL sızmasını engelleyen mekanizma useFilterUrlSync'teki interactionCount guard +
 *  unchangedInitialKeys excludeKeys sistemidir. */
const extractServerSelected = (data: FiltersData): Partial<SelectedFilters> => {
  const result: Partial<SelectedFilters> = {};
  for (const group of data.filters.general) {
    if (group.type === "list") {
      const key = group.key as keyof SelectedFilters;
      if (STRING_FILTER_KEYS.has(key)) {
        (result[key] as string[]) = (group.value as (string | number)[]).map(
          String,
        );
      } else {
        (result[key] as number[]) = (group.value as (string | number)[]).map(
          Number,
        );
      }
    } else if (group.type === "input" && group.key === "price") {
      const v = group.value as { min: number | null; max: number | null };
      result.price = {
        ...(v.min != null ? { min: v.min } : {}),
        ...(v.max != null ? { max: v.max } : {}),
      };
    }
  }
  return result;
};

const filtersSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    setSelected: (state, action: PayloadAction<Partial<SelectedFilters>>) => {
      state.selected = { ...state.selected, ...action.payload };
      // Bu reducer'ı yalnızca useFilterUrlSync init'i çağırıyor (slugControl
      // initialSelected + URL merge'ü), yani buraya düşen değer sayfa bağlamı.
      // Korunan gruplardan biri doluysa minimum-1 kuralı devreye girer.
      if (countGuardedSelections(state.selected) > 0) {
        state.guardedContextSeeded = true;
      }
    },
    /** useFilterUrlSync init'i her applyUrlToState'te çağırır: o an URL'de yazan
     *  filtre key'leri. getFilters bağlam merge'ü bu key'leri atlar. */
    setUrlOwnedKeys: (
      state,
      action: PayloadAction<(keyof SelectedFilters)[]>,
    ) => {
      state.urlOwnedKeys = action.payload;
    },
    toggleListFilter: (
      state,
      action: PayloadAction<{ key: ListFilterKey; value: string }>,
    ) => {
      const { key, value } = action.payload;

      const applyToggle = (target: SelectedFilters) => {
        if (STRING_FILTER_KEYS.has(key)) {
          const current = target[key] as string[];
          const exists = current.includes(value);
          (target[key] as string[]) = exists
            ? current.filter((v) => v !== value)
            : [...current, value];
        } else {
          const numericValue = Number(value);
          const current = target[key] as number[];
          const exists = current.includes(numericValue);
          (target[key] as number[]) = exists
            ? current.filter((v) => v !== numericValue)
            : [...current, numericValue];
        }
      };

      applyToggle(state.selected);
      applyToggle(state.serverSelected);
      state.userInteractionCount += 1;
    },
    toggleBooleanFilter: (state, action: PayloadAction<BooleanFilterKey>) => {
      (state.selected[action.payload] as boolean) =
        !state.selected[action.payload];
      (state.serverSelected[action.payload] as boolean) =
        !state.serverSelected[action.payload];
      state.userInteractionCount += 1;
    },
    toggleSpecialFilter: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      const toggle = (target: SelectedFilters) => {
        if (key === "all") {
          target.special_filters = [];
          return;
        }
        const exists = target.special_filters.includes(key);
        target.special_filters = exists
          ? target.special_filters.filter((k) => k !== key)
          : [...target.special_filters, key];
      };
      toggle(state.selected);
      toggle(state.serverSelected);
      state.userInteractionCount += 1;
    },
    setSort: (state, action: PayloadAction<string>) => {
      state.selected.sort = action.payload;
      state.serverSelected.sort = action.payload;
      state.userInteractionCount += 1;
    },
    setPriceFilter: (
      state,
      action: PayloadAction<{ min?: number; max?: number }>,
    ) => {
      state.selected.price = action.payload;
      state.serverSelected.price = action.payload;
      state.userInteractionCount += 1;
    },
    resetFilters: (state) => {
      state.selected = defaultSelected;
      state.serverSelected = defaultSelected;
      state.serverSelectedReady = false;
      state.serverSelectedSlug = null;
      state.userInteractionCount = 0;
      state.urlOwnedKeys = [];
      state.guardedContextSeeded = false;
    },
    /** Kullanıcı "Tüm Filtreleri Temizle" butonuna bastığında çağrılır.
     *  data/generalData'ya dokunmaz — filtre skeleton'ı gösterilmez.
     *  payload: sayfa bağlamından gelen initialSelected (item_ids, gender vb.)
     *  — bunlar korunur, yalnızca kullanıcının eklediği filtreler temizlenir. */
    clearSelectedOnly: (state, action: PayloadAction<Partial<SelectedFilters> | undefined>) => {
      const base = action.payload ?? {};
      const restored = { ...defaultSelected, ...base };
      state.selected = restored;
      state.serverSelected = restored;
      state.serverSelectedReady = false;
      state.serverSelectedSlug = null;
      state.userInteractionCount = 0;
      // URL de temizleniyor (handleClearFilters replaceState atıyor) — bağlam
      // merge'ü yeniden serbest kalmalı, aksi halde kategori kutuları işaretsiz kalır.
      state.urlOwnedKeys = [];
      // Temizleme bağlamı (initialSelected) geri yüklüyor — kural bağlamla
      // birlikte yeniden hesaplanmalı.
      state.guardedContextSeeded = countGuardedSelections(restored) > 0;
    },
    /** Slug değişiminde çağrılır — eski slug'ın serverSelected'ı yeni slug'a sızmasın.
     *  selected'a DOKUNMAZ (onu useFilterUrlSync init'i pathname değişince yönetir). */
    resetServerSelected: (state) => {
      state.serverSelected = defaultSelected;
      state.serverSelectedReady = false;
      state.serverSelectedSlug = null;
      state.userInteractionCount = 0;
    },
    resetFiltersData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFilters.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getFilters.fulfilled, (state, action) => {
        state.status = "success";
        state.data = action.payload.data;
        // Bu yanıtın hangi slug için olduğunu request'ten oku.
        const reqSlug =
          (action.meta.arg as { __slug?: string })?.__slug ?? null;
        const extracted = extractServerSelected(action.payload.data);
        state.serverSelected = { ...defaultSelected, ...extracted };
        state.serverSelectedReady = true;
        state.serverSelectedSlug = reqSlug;

        // Bağlam key'lerini (category, category_groups vb.) selected'a da merge et —
        // bunlar sidebar'da seçili görünsün. Kullanıcı henüz filtrelere dokunmamışsa
        // (userInteractionCount === 0) yazılır; dokunduktan sonra override edilmez.
        if (state.userInteractionCount === 0) {
          const CONTEXT_KEYS = [
            "category",
            "category_groups",
            "category_sub_groups",
            "gender",
          ] as const;
          for (const key of CONTEXT_KEYS) {
            // URL'de açıkça yazan key backend bağlamıyla EZİLMEZ. Remount'ta
            // (arama sayfasından geri dönüş vb.) userInteractionCount 0'a
            // düştüğü için ?gender=7 bağlam gender'ıyla siliniyordu.
            if (state.urlOwnedKeys.includes(key)) continue;
            const val = extracted[key] as number[] | undefined;
            if (val?.length) {
              (state.selected[key] as number[]) = val;
              // Backend bağlamı korunan gruplardan birini doldurduysa
              // minimum-1 kuralı bu sayfa için geçerli.
              if (isMinimumGuardedKey(key)) state.guardedContextSeeded = true;
            }
          }
        }
      })
      .addCase(getFilters.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload ?? "Filtreler yüklenemedi.";
      })
      .addCase(getGeneralFilters.pending, (state) => {
        state.generalStatus = "loading";
      })
      .addCase(getGeneralFilters.fulfilled, (state, action) => {
        state.generalStatus = "success";
        state.generalData = action.payload.data;
      })
      .addCase(getGeneralFilters.rejected, (state) => {
        state.generalStatus = "error";
      });
  },
});

export const {
  setSelected,
  setUrlOwnedKeys,
  toggleListFilter,
  toggleBooleanFilter,
  toggleSpecialFilter,
  setSort,
  setPriceFilter,
  resetFilters,
  resetServerSelected,
  resetFiltersData,
  clearSelectedOnly,
} = filtersSlice.actions;

export default filtersSlice.reducer;
